/**
 * The announcement calls and fields added in wave w1023c (audience, expiry,
 * pin, seen-by), kept beside the components that use them.
 *
 * `services/api.ts` and `types/api.ts` are integration files; until the PM
 * folds these into `api.community.*` and `CommunityPost`, this module is the
 * one place the client names these routes and fields. Every call goes through
 * the shared `apiClient`, so auth, locale and error handling are the app's own.
 */
import { apiClient } from '@/services/api';
import type { ApiResponse, CommunityPost } from '@/types/api';
import type { SendAudience } from '@/types/formSends';

/** What an announcement carries since w1023c. Older rows omit every field. */
export interface AnnouncementFields {
  /** The poll audience shape; `null` or absent = all families. */
  audience?: SendAudience | null;
  /** Families in the audience when it was posted; `null` = the count failed. */
  audience_size?: number | null;
  /** POST response only: parents the in-app notice reached, or null when the fan-out failed. */
  notified?: number | null;
  /** POST response only: a requested pin did not take. */
  pin_failed?: boolean;
  /** Families with a "seen" record (staff list only). */
  seen_count?: number;
  /** The last calendar day it shows, YYYY-MM-DD; it leaves at the end of that day. */
  expires_on?: string | null;
  /** At most one announcement is pinned; it renders first. */
  pinned?: boolean;
}

export type Announcement = CommunityPost & AnnouncementFields;

export interface NewAnnouncement {
  title?: string | null;
  body: string;
  /** `null` = all families. */
  audience: SendAudience | null;
  expires_on: string | null;
  pinned: boolean;
}

/** A composer draft as the feed would hold it the moment it is posted (the Preview). */
export function draftAnnouncement(
  { title, body, pinned }: { title: string; body: string; pinned: boolean },
  now: Date = new Date(),
): Announcement {
  return {
    _id: 'preview',
    kind: 'announcement',
    author_family_id: null,
    author_display: 'TRF',
    anonymous: false,
    title: title.trim() || null,
    body: body.trim(),
    status: 'visible',
    guardian_flagged: false,
    reactions: { heart: [], hug: [], celebrate: [] },
    created_at: now.toISOString(),
    pinned,
  };
}

export const announcementApi = {
  /** One request per rendered feed page: which announcements were on screen. */
  markSeen: async (ids: string[]): Promise<ApiResponse<{ recorded: number }>> => {
    const response = await apiClient.post<ApiResponse<{ recorded: number }>>('/community/posts/seen', { ids });
    return response.data;
  },

  /** Staff list: live announcements, pinned first, with seen counts. */
  list: async (): Promise<ApiResponse<Announcement[]>> => {
    const response = await apiClient.get<ApiResponse<Announcement[]>>('/trfer/community/announcements');
    return response.data;
  },

  create: async (data: NewAnnouncement): Promise<ApiResponse<Announcement>> => {
    const response = await apiClient.post<ApiResponse<Announcement>>('/trfer/community/announcements', data);
    return response.data;
  },

  /** Pinning one unpins any other. */
  setPinned: async (id: string, pinned: boolean): Promise<ApiResponse<Announcement>> => {
    const response = await apiClient.post<ApiResponse<Announcement>>(
      `/trfer/community/announcements/${id}/pin`,
      { pinned },
    );
    return response.data;
  },
};
